<template>
  <v-dialog v-model="internalValue" max-width="700px" scrollable transition="dialog-bottom-transition">
    <v-card v-if="item" class="rounded-xl pa-2">
      <!-- Modal Header -->
      <v-card-title class="d-flex align-center justify-space-between pt-4 px-4 pb-2">
        <div class="d-flex align-center">
          <v-avatar color="primary lighten-5" size="44" class="mr-3">
            <v-icon color="primary">{{ icons.mdiDatabaseEdit }}</v-icon>
          </v-avatar>
          <div>
            <div class="text-caption font-weight-bold text-uppercase primary--text tracking-wider">
              SURCHARGE MANUELLE
            </div>
            <h2 class="text-h5 font-weight-black primary--text text-uppercase mb-0">
              {{ item.site_name }}
            </h2>
          </div>
        </div>
        <v-btn icon @click="internalValue = false">
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text class="px-4 py-2">
        <p class="text-caption grey--text text--darken-1 mb-4">
          Les valeurs importées restent intactes. Les overides prennent priorité quand ils sont renseignés.
        </p>

        <!-- Grid of Edit Cards for Fields -->
        <v-row dense>
          <v-col
            v-for="field in editableFields"
            :key="field.key"
            cols="12"
            sm="6"
          >
            <v-card outlined class="pa-3 rounded-lg metric-edit-card fill-height">
              <div class="d-flex align-center justify-space-between mb-3">
                <span class="font-weight-bold text-body-2 text-uppercase primary--text">{{ getFieldLabel(field.key) }}</span>
                <v-chip 
                  x-small 
                  :color="isOverridden(field.key) ? 'warning' : 'grey lighten-1'" 
                  :class="isOverridden(field.key) ? 'white--text font-weight-bold' : 'white--text font-weight-bold'"
                >
                  {{ isOverridden(field.key) ? 'Surchargée' : 'Importée' }}
                </v-chip>
              </div>
              
              <div class="d-flex align-end justify-space-between mt-auto">
                <div>
                  <span class="text-caption grey--text d-block mb-1">
                    Valeur importée: <strong class="grey--text text--darken-2">{{ formatValue(item[field.key], field.key) }}</strong>
                  </span>
                <v-text-field
                  v-model.number="overrideForm[field.key]"
                  type="number"
                  dense
                  outlined
                  hide-details
                  placeholder="Auto"
                  class="override-input"
                  style="max-width: 140px;"
                ></v-text-field>
                </div>

                <div class="text-right pl-3 pb-1">
                  <span class="text-caption grey--text text-uppercase d-block" style="font-size: 0.65rem !important;">
                    VALEUR FINALE
                  </span>
                  <span :class="['font-weight-black text-body-1', isOverridden(field.key) ? 'warning--text' : 'primary--text']">
                    {{ formatValue(getFinalRawValue(field.key), field.key) }}
                  </span>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Modal Actions -->
      <v-card-actions class="d-flex align-center justify-space-between px-4 py-3">
        <v-btn text color="grey darken-1" class="text-capitalize" @click="clearAllOverrides">
          Tout effacer
        </v-btn>
        <div>
          <v-btn text color="grey darken-2" class="mr-2" @click="internalValue = false">Annuler</v-btn>
          <v-btn color="primary" class="px-6 rounded-lg font-weight-bold shadow-btn" :loading="saving" @click="saveOverride">
            ENREGISTRER
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import autoEmailService from "@/services/autoEmailReport.service";
import { mdiDatabaseEdit, mdiClose } from "@mdi/js";

export default {
  name: "GssOverrideModal",
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: () => null },
    datasetName: { type: String, required: true },
  },
  data() {
    return {
      icons: { mdiDatabaseEdit, mdiClose },
      saving: false,
      overrideForm: {},
    };
  },
  computed: {
    internalValue: {
      get() { return this.value; },
      set(val) { this.$emit("input", val); },
    },
    editableFields() {
      if (!this.item) return [];
      return Object.keys(this.item)
        .filter((k) => !["id", "site_id", "site_name", "partner_name", "year", "month", "date_val", "date", "raw_data", "manual_overrides", "created_at", "updated_at", "last_synced_at", "key_sm", "key_sj", "key_aj"].includes(k))
        .map((k) => ({
          key: k,
        }));
    },
  },
  watch: {
    item: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const initialForm = {};
          const existing = newVal.manual_overrides || {};
          this.editableFields.forEach((field) => {
            initialForm[field.key] = existing[field.key] !== undefined ? existing[field.key] : null;
          });
          this.overrideForm = initialForm;
        }
      }
    }
  },
  methods: {
    isValidNumber(val) {
      return val !== undefined && val !== null && val !== "" && !Number.isNaN(val);
    },
    getFieldLabel(key) {
      const frLabels = {
        "site_name": "SITE",
        "partner_name": "PARTENAIRE",
        "year": "ANNÉE",
        "month": "MOIS",
        "date_val": "DATE",
        "date": "DATE",
        "ca_reel": "CA RÉEL",
        "objectif_ca": "OBJECTIF CA",
        "objectif_rpm": "OBJECTIF RPM",
        "objectif_sessions": "OBJECTIF SESSIONS",
        "ca": "CA",
        "rpm": "RPM",
        "n_ca": "N/CA",
        "ca_n1": "CA N-1",
        "rpm_n1": "RPM N-1",
        "n_ca_n1": "N/CA N-1",
        "pvs_wys": "PVS WYS",
        "sessions_wys": "SESSIONS WYS",
        "pvs_wys_n1": "PVS WYS N-1",
        "sessions_wys_n1": "SESSIONS WYS N-1",
        "sessions": "SESSIONS",
        "montant": "MONTANT",
        "budget": "BUDGET"
      };
      if (frLabels[key]) return frLabels[key];
      return key.toUpperCase().replace(/_/g, " ");
    },
    formatValue(val, key) {
      if (val === null || val === undefined || val === "") return "-";
      if (key === "date_val" || key === "date") {
        const isoStr = String(val).split("T")[0];
        const parts = isoStr.split("-");
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return val;
      }
      if (typeof val === "number") {
        const formatted = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(val);
        if (["ca", "ca_reel", "objectif_ca", "ca_n1", "montant", "budget"].includes(key)) {
          return `${formatted} €`;
        }
        return formatted;
      }
      return val;
    },
    isOverridden(key) {
      return this.isValidNumber(this.overrideForm[key]);
    },
    getFinalRawValue(key) {
      if (!this.item) return null;
      if (this.isValidNumber(this.overrideForm[key])) {
        return this.overrideForm[key];
      }
      return this.item[key] !== undefined ? this.item[key] : null;
    },
    clearAllOverrides() {
      const resetForm = {};
      this.editableFields.forEach((field) => {
        resetForm[field.key] = null;
      });
      this.overrideForm = resetForm;
    },
    async saveOverride() {
      if (!this.item) return;
      this.saving = true;
      try {
        const cleanOverrides = Object.keys(this.overrideForm).reduce((acc, key) => {
          const val = this.overrideForm[key];
          acc[key] = this.isValidNumber(val) ? val : null;
          return acc;
        }, {});
        
        await autoEmailService.patchGssOverride({
          dataset_name: this.datasetName,
          id: this.item.id,
          overrides: cleanOverrides,
        });
        this.internalValue = false;
        this.$emit("saved");
      } catch (err) {
        console.error("Erreur lors de la sauvegarde :", err);
        this.$emit("error", "Erreur lors de la sauvegarde");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.shadow-btn {
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3) !important;
}

.metric-edit-card {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
}

.override-input >>> .v-input__control {
  background-color: white;
  border-radius: 8px;
}
</style>
